<script lang="ts">
  import { onMount } from "svelte";

  import { resetAdminGate } from "../../lib/admin";
  import { AUTH_EVENT, isSignedIn } from "../../lib/auth";
  import { APP_NAME, SITE_ENABLED } from "../../lib/brand";
  import { APP_VERSION } from "../../lib/buildinfo";
  import { isNativeIos } from "../../lib/config";
  import { router, go } from "../../lib/router.svelte";
  import { AppBar } from "../../lib/ui";
  import Account from "./Account.svelte";
  import Billing from "./Billing.svelte";
  import Callback from "./Callback.svelte";
  import Cancelled from "./Cancelled.svelte";
  import Download from "./Download.svelte";
  import Home from "./Home.svelte";
  import Login from "./Login.svelte";
  import NotFound from "./NotFound.svelte";
  import Pricing from "./Pricing.svelte";
  import Privacy from "./Privacy.svelte";
  import Redeem from "./Redeem.svelte";
  import Refund from "./Refund.svelte";
  import Success from "./Success.svelte";
  import Terms from "./Terms.svelte";
  import Usage from "./Usage.svelte";

  type RouteName =
    | "home"
    | "pricing"
    | "download"
    | "account"
    | "usage"
    | "billing"
    | "redeem"
    | "success"
    | "cancelled"
    | "terms"
    | "privacy"
    | "refund"
    | "login"
    | "callback"
    | "notFound";

  const PATHS: Record<string, RouteName> = {
    "/": "home",
    "/pricing": "pricing",
    "/download": "download",
    "/account": "account",
    "/account/usage": "usage",
    "/account/billing": "billing",
    "/redeem": "redeem",
    "/success": "success",
    "/cancelled": "cancelled",
    "/terms": "terms",
    "/privacy": "privacy",
    "/refund": "refund",
    "/login": "login",
    "/auth/callback": "callback",
  };

  // On-prem (Neos Hub) builds ship no public marketing/legal/download/billing
  // surfaces — only the admin console + remote control. Any such route falls
  // through to NotFound; "/" renders a minimal landing (see Home.svelte).
  let name = $derived<RouteName>(
    ((n: RouteName): RouteName =>
      !SITE_ENABLED && n !== "home" && n !== "login" && n !== "callback"
        ? "notFound"
        : n)(PATHS[router.path] ?? "notFound"),
  );
  let signedIn = $state(false);

  // Drop the cached admin verdict on any sign-in / sign-out so the
  // IdentityMenu re-probes with the new token — and the Admin row appears
  // / disappears without a hard refresh.
  function onAuthChange() {
    resetAdminGate();
    signedIn = isSignedIn();
  }

  onMount(() => {
    signedIn = isSignedIn();
    window.addEventListener(AUTH_EVENT, onAuthChange);
    return () => window.removeEventListener(AUTH_EVENT, onAuthChange);
  });
</script>

<div class="flex h-dvh flex-col overflow-hidden">
  <!-- One AppBar across all three sections. SiteShell uses the `center`
       snippet to expose its public marketing links (Pricing / Download)
       inline on desktop; on mobile they move into the footer. Section
       navigation between Home / Remote / Admin lives in the AppBar's
       identity menu so it's always one tap away from any surface. -->
  <AppBar>
    {#snippet center()}
      {#if SITE_ENABLED}
      <nav class="flex items-center gap-5 text-sm text-ink-dim">
        <a
          href="/pricing"
          onclick={(e) => go(e, "/pricing")}
          class={`transition hover:text-ink ${name === "pricing" ? "text-ink" : ""}`}
        >
          Pricing
        </a>
        {#if !isNativeIos()}
          <a
            href="/download"
            onclick={(e) => go(e, "/download")}
            class={`transition hover:text-ink ${name === "download" ? "text-ink" : ""}`}
          >
            Download
          </a>
        {/if}
        {#if signedIn}
          <a
            href="/account"
            onclick={(e) => go(e, "/account")}
            class={`transition hover:text-ink ${name === "account" || name === "usage" || name === "billing" ? "text-ink" : ""}`}
          >
            Account
          </a>
        {/if}
      </nav>
      {/if}
    {/snippet}
  </AppBar>

  <main class="flex-1 min-h-0 overflow-y-auto">
    {#if name === "home"}
      <Home />
    {:else if name === "pricing"}
      <Pricing />
    {:else if name === "download"}
      <Download />
    {:else if name === "account"}
      <Account />
    {:else if name === "usage"}
      <Usage />
    {:else if name === "billing"}
      <Billing />
    {:else if name === "redeem"}
      <Redeem />
    {:else if name === "success"}
      <Success />
    {:else if name === "cancelled"}
      <Cancelled />
    {:else if name === "terms"}
      <Terms />
    {:else if name === "privacy"}
      <Privacy />
    {:else if name === "refund"}
      <Refund />
    {:else if name === "login"}
      <Login />
    {:else if name === "callback"}
      <Callback />
    {:else}
      <NotFound />
    {/if}
  </main>

  <footer
    class="shrink-0 px-6 pt-6 border-t border-line text-xs text-ink-faint flex flex-wrap items-center gap-x-4 gap-y-2"
    style="padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));"
  >
    <span>© {SITE_ENABLED ? "Optioway" : APP_NAME} · v{APP_VERSION}</span>
    {#if SITE_ENABLED}
      <nav class="flex flex-wrap items-center gap-x-4 gap-y-2">
        <a href="/pricing" onclick={(e) => go(e, "/pricing")} class="hover:text-ink-dim">Pricing</a>
        <a href="/terms" onclick={(e) => go(e, "/terms")} class="hover:text-ink-dim">Terms</a>
        <a href="/privacy" onclick={(e) => go(e, "/privacy")} class="hover:text-ink-dim">Privacy</a>
        <a href="/refund" onclick={(e) => go(e, "/refund")} class="hover:text-ink-dim">Refunds</a>
      </nav>
    {/if}
  </footer>
</div>
